/**
 * UYARI: Bu hook'u doğrudan componentlerde kullanmayın!
 * Bunun yerine PermissionsProvider ile context üzerinden erişin:
 * import { usePermissionsContext } from '../providers/PermissionsProvider';
 * const { hasPermission } = usePermissionsContext();
 */
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  selectPermissionMap,
  selectPermissionsLoading,
  selectPermissionsError,
  selectPermissionsInitialized,
  fetchPermissions,
  clearPermissions,
} from '../store/slices/permissionSlice';
import { selectUser, selectIsAuthenticated } from '../store/slices/authSlice';
import {
  SCREENS,
  ACTIONS,
  USER_ROLES,
  isProtectedRole,
} from '../constants/permissions';

export const usePermissions = () => {
  const dispatch = useDispatch();

  // Safe selectors with fallbacks
  const user = useSelector(selectUser) || null;
  const isAuthenticated = useSelector(selectIsAuthenticated) || false;
  const permissionMap = useSelector(selectPermissionMap) || {};
  const isLoading = useSelector(selectPermissionsLoading) || false;
  const error = useSelector(selectPermissionsError) || null;
  const initialized = useSelector(selectPermissionsInitialized) || false;
  const fetchInFlight = useRef(false);

  useEffect(() => {
    if (
      isAuthenticated &&
      user &&
      !initialized &&
      !isLoading &&
      !fetchInFlight.current
    ) {
      fetchInFlight.current = true;
      dispatch(
        fetchPermissions({
          role: user.role,
          companyId: user.companyId,
        })
      ).finally(() => {
        fetchInFlight.current = false;
      });
    }
  }, [
    isAuthenticated,
    user?.role,
    user?.companyId,
    initialized,
    isLoading,
    dispatch,
  ]);

  useEffect(() => {
    if (!isAuthenticated && initialized) {
      dispatch(clearPermissions());
    }
  }, [isAuthenticated, initialized, dispatch]);

  const hasPermission = useCallback(
    (screen, action, companyId = null) => {
      if (!user || !initialized) return false;

      const targetCompanyId = companyId || user.companyId;
      const key = `${screen}_${targetCompanyId}`;
      const permission = permissionMap[key];

      const actionMap = {
        can_create: 'canCreate',
        can_view: 'canView',
        can_edit: 'canEdit',
        can_delete: 'canDelete',
        can_download: 'canDownload',
      };

      const mappedAction = actionMap[action] || action;

      if (!permission) {
        const alternativeKey = Object.keys(permissionMap).find(k =>
          k.startsWith(`${screen}_`)
        );
        if (alternativeKey) {
          const altPermission = permissionMap[alternativeKey];
          return altPermission ? altPermission[mappedAction] : false;
        }
      }

      return permission ? permission[mappedAction] : false;
    },
    [user, initialized, permissionMap]
  );

  const hasPermissions = useCallback(
    checks => {
      return checks.every(({ screen, action, companyId }) =>
        hasPermission(screen, action, companyId)
      );
    },
    [hasPermission]
  );

  const getScreenPermissions = useCallback(
    (screen, companyId = null) => {
      if (!user || !initialized) {
        return {
          [ACTIONS.CREATE]: false,
          [ACTIONS.EDIT]: false,
          [ACTIONS.VIEW]: false,
          [ACTIONS.DELETE]: false,
          canCreate: false,
          canEdit: false,
          canView: false,
          canDelete: false,
        };
      }

      const targetCompanyId = companyId || user.companyId;
      const key = `${screen}_${targetCompanyId}`;
      const permission = permissionMap[key];

      const actionMap = {
        can_create: 'canCreate',
        can_view: 'canView',
        can_edit: 'canEdit',
        can_delete: 'canDelete',
        can_download: 'canDownload',
      };

      if (permission) {
        return {
          [ACTIONS.CREATE]: permission[actionMap[ACTIONS.CREATE]] || false,
          [ACTIONS.EDIT]: permission[actionMap[ACTIONS.EDIT]] || false,
          [ACTIONS.VIEW]: permission[actionMap[ACTIONS.VIEW]] || false,
          [ACTIONS.DELETE]: permission[actionMap[ACTIONS.DELETE]] || false,
          [ACTIONS.DOWNLOAD]: permission[actionMap[ACTIONS.DOWNLOAD]] || false,
          canCreate: permission[actionMap[ACTIONS.CREATE]] || false,
          canEdit: permission[actionMap[ACTIONS.EDIT]] || false,
          canView: permission[actionMap[ACTIONS.VIEW]] || false,
          canDelete: permission[actionMap[ACTIONS.DELETE]] || false,
          canDownload: permission[actionMap[ACTIONS.DOWNLOAD]] || false,
        };
      }

      return {
        [ACTIONS.CREATE]: false,
        [ACTIONS.EDIT]: false,
        [ACTIONS.VIEW]: false,
        [ACTIONS.DELETE]: false,
        [ACTIONS.DOWNLOAD]: false,
        canCreate: false,
        canEdit: false,
        canView: false,
        canDelete: false,
        canDownload: false,
      };
    },
    [user, initialized, permissionMap]
  );

  const getAccessibleScreens = useCallback(() => {
    if (!user || !initialized) return [];

    if (user.role === 'ROOT') {
      return Object.values(SCREENS);
    }

    const accessibleScreens = [];
    Object.values(SCREENS).forEach(screen => {
      if (hasPermission(screen, ACTIONS.VIEW)) {
        accessibleScreens.push(screen);
      }
    });

    return accessibleScreens;
  }, [user, initialized, hasPermission]);

  const refreshPermissions = useCallback(() => {
    if (user && !fetchInFlight.current) {
      fetchInFlight.current = true;
      dispatch(
        fetchPermissions({
          role: user.role,
          companyId: user.companyId,
        })
      ).finally(() => {
        fetchInFlight.current = false;
      });
    }
  }, [user?.role, user?.companyId, dispatch]);

  return {
    hasPermission,
    hasPermissions,
    getScreenPermissions,
    getAccessibleScreens,
    refreshPermissions,

    isLoading,
    error,
    initialized,

    user,
    isAuthenticated,
  };
};

// Alt hook'lar da doğrudan Redux kullanacak şekilde düzenlendi
export const useCanCreate = (screen, companyId = null) => {
  const { hasPermission } = usePermissions();
  return hasPermission(screen, ACTIONS.CREATE, companyId);
};

export const useCanEdit = (screen, companyId = null) => {
  const { hasPermission } = usePermissions();
  return hasPermission(screen, ACTIONS.EDIT, companyId);
};

export const useCanView = (screen, companyId = null) => {
  const { hasPermission } = usePermissions();
  return hasPermission(screen, ACTIONS.VIEW, companyId);
};

export const useCanDelete = (screen, companyId = null) => {
  const { hasPermission } = usePermissions();
  return hasPermission(screen, ACTIONS.DELETE, companyId);
};

export const useCanDownload = (screen, companyId = null) => {
  const { hasPermission } = usePermissions();
  return hasPermission(screen, ACTIONS.DOWNLOAD, companyId);
};

export const useCompanyPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.COMPANIES, companyId);
};

export const useUserPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.USERS, companyId);
};

export const useGroupPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.GROUPS, companyId);
};

export const useTeamPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.TEAMS, companyId);
};

export const useTeamMemberPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.TEAM_MEMBERS, companyId);
};

export const useLeaveRequestPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.LEAVE_REQUESTS, companyId);
};

export const useSettingsPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.SETTINGS, companyId);
};

export const useFirmManagementPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.FIRM_MANAGEMENTS, companyId);
};

export const useContractsPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.CONTRACTS, companyId);
};

export const useOffersPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.OFFERS, companyId);
};

export const useReportManagementPermissions = (companyId = null) => {
  const { getScreenPermissions } = usePermissions();
  return getScreenPermissions(SCREENS.REPORT_MANAGEMENT, companyId);
};
